import axios from 'axios';

const LA_ASSESSOR_BASE_URL = 'https://portal.assessor.lacounty.gov';
const LA_GIS_BASE_URL = 'https://public.gis.lacounty.gov/public/rest/services';
const LA_OPEN_DATA_URL = 'https://data.lacounty.gov';

const DEFAULT_TIMEOUT = 30000;

// Optional: Add your Socrata app token here for better rate limits
const SOCRATA_APP_TOKEN = process.env.REACT_APP_SOCRATA_APP_TOKEN || null;

class LACountyAPIError extends Error {
  constructor(message, code, details) {
    super(message);
    this.name = 'LACountyAPIError';
    this.code = code;
    this.details = details;
  }
}

const handleAPIError = (error, context) => {
  console.error(`LA County API Error in ${context}:`, error);

  if (error.response) {
    throw new LACountyAPIError(
      `API request failed: ${error.response.statusText}`,
      error.response.status,
      error.response.data
    );
  } else if (error.request) {
    throw new LACountyAPIError(
      'No response received from LA County API',
      'NO_RESPONSE',
      error.request
    );
  } else {
    throw new LACountyAPIError(
      `Request setup failed: ${error.message}`,
      'REQUEST_ERROR',
      error
    );
  }
};

export const searchByAddress = async (address) => {
  try {
    // Try direct address search in the GIS API
    // The SitusAddress field contains the property address
    const response = await axios.get(
      `${LA_GIS_BASE_URL}/LACounty_Cache/LACounty_Parcel/MapServer/0/query`,
      {
        params: {
          where: `UPPER(SitusAddress) LIKE UPPER('%${address}%')`,
          outFields: '*',
          returnGeometry: true,
          f: 'json',
          outSR: 4326,
          resultRecordCount: 10,
        },
        timeout: DEFAULT_TIMEOUT,
      }
    );

    if (response.data.features && response.data.features.length > 0) {
      return {
        success: true,
        results: response.data.features.map((feature) => ({
          apn: feature.attributes.APN,
          address: feature.attributes.SitusAddress,
          useType: feature.attributes.UseType,
          geometry: feature.geometry,
          attributes: feature.attributes,
        })),
        count: response.data.features.length,
      };
    }

    return {
      success: true,
      results: [],
      count: 0,
    };
  } catch (error) {
    handleAPIError(error, 'searchByAddress');
  }
};

export const searchByAPN = async (apn) => {
  try {
    const formattedAPN = apn.replace(/[^0-9]/g, '');

    const response = await axios.get(
      `${LA_ASSESSOR_BASE_URL}/api/parcel/${formattedAPN}`,
      {
        timeout: DEFAULT_TIMEOUT,
        headers: {
          Accept: 'application/json',
        },
      }
    );

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    handleAPIError(error, 'searchByAPN');
  }
};

export const getParcelBoundary = async (apn) => {
  try {
    const response = await axios.get(
      `${LA_GIS_BASE_URL}/LACounty_Cache/LACounty_Parcel/MapServer/0/query`,
      {
        params: {
          where: `APN='${apn}'`,
          outFields: '*',
          returnGeometry: true,
          f: 'json',
          outSR: 4326,
        },
        timeout: DEFAULT_TIMEOUT,
      }
    );

    if (!response.data.features || response.data.features.length === 0) {
      throw new LACountyAPIError(
        'No parcel found with given APN',
        'NOT_FOUND',
        { apn }
      );
    }

    return {
      success: true,
      features: response.data.features,
      geometry: response.data.features[0].geometry,
      attributes: response.data.features[0].attributes,
    };
  } catch (error) {
    if (error instanceof LACountyAPIError) throw error;
    handleAPIError(error, 'getParcelBoundary');
  }
};

export const getPropertyInfo = async (apn) => {
  try {
    // Clean the APN - remove any dashes or spaces
    const cleanAPN = apn.replace(/[^0-9]/g, '');
    console.log('Searching for property info with APN:', cleanAPN);

    const response = await axios.get(
      `${LA_GIS_BASE_URL}/LACounty_Dynamic/LACounty_Parcel/MapServer/0/query`,
      {
        params: {
          where: `AIN='${cleanAPN}' OR APN='${cleanAPN}'`,
          outFields: '*',
          returnGeometry: false,
          f: 'json',
        },
        timeout: DEFAULT_TIMEOUT,
      }
    );

    console.log('Property info response:', response.data);

    // Check if there's an error in the response
    if (response.data.error) {
      console.error('LA County API error:', response.data.error);
      throw new LACountyAPIError(
        `LA County API error: ${response.data.error.message || 'Unknown error'}`,
        'API_ERROR',
        response.data.error
      );
    }

    if (!response.data.features || response.data.features.length === 0) {
      // Try with the original APN format
      const response2 = await axios.get(
        `${LA_GIS_BASE_URL}/LACounty_Dynamic/LACounty_Parcel/MapServer/0/query`,
        {
          params: {
            where: `AIN='${apn}' OR APN='${apn}'`,
            outFields: '*',
            returnGeometry: false,
            f: 'json',
          },
          timeout: DEFAULT_TIMEOUT,
        }
      );

      if (!response2.data.features || response2.data.features.length === 0) {
        throw new LACountyAPIError('No property info found', 'NOT_FOUND', {
          apn,
          cleanAPN,
        });
      }

      response.data = response2.data;
    }

    const attributes = response.data.features[0].attributes;

    return {
      success: true,
      propertyInfo: {
        apn: attributes.AIN || attributes.APN || apn,
        address:
          attributes.SitusAddress ||
          attributes.SitusFullAddress ||
          attributes.Address,
        useType:
          attributes.UseType || attributes.UseCode || attributes.PropertyType,
        yearBuilt: attributes.YearBuilt || attributes.EffectiveYearBuilt,
        squareFeet:
          attributes.SQFTMain || attributes.BuildingSqFt || attributes.SQFT,
        units: attributes.Units || attributes.NumberOfUnits || 0,
        landValue: attributes.Roll_LandValue || attributes.LandValue || 0,
        improvementValue:
          attributes.Roll_ImpValue || attributes.ImprovementValue || 0,
        totalValue:
          (attributes.Roll_LandValue || attributes.LandValue || 0) +
          (attributes.Roll_ImpValue || attributes.ImprovementValue || 0),
      },
    };
  } catch (error) {
    if (error instanceof LACountyAPIError) throw error;
    handleAPIError(error, 'getPropertyInfo');
  }
};

export const searchNearbyParcels = async (
  latitude,
  longitude,
  radius = 500
) => {
  try {
    const response = await axios.get(
      `${LA_GIS_BASE_URL}/LACounty_Cache/LACounty_Parcel/MapServer/0/query`,
      {
        params: {
          geometry: JSON.stringify({
            x: longitude,
            y: latitude,
            spatialReference: { wkid: 4326 },
          }),
          geometryType: 'esriGeometryPoint',
          spatialRel: 'esriSpatialRelIntersects',
          distance: radius,
          units: 'esriSRUnit_Meter',
          outFields: 'APN,SitusAddress,UseType',
          returnGeometry: true,
          f: 'json',
          outSR: 4326,
        },
        timeout: DEFAULT_TIMEOUT,
      }
    );

    return {
      success: true,
      parcels: response.data.features || [],
      count: response.data.features ? response.data.features.length : 0,
    };
  } catch (error) {
    handleAPIError(error, 'searchNearbyParcels');
  }
};

export const batchGetPropertyInfo = async (apnList, chunkSize = 10) => {
  const results = [];
  const errors = [];

  const chunks = [];
  for (let i = 0; i < apnList.length; i += chunkSize) {
    chunks.push(apnList.slice(i, i + chunkSize));
  }

  for (const chunk of chunks) {
    const chunkPromises = chunk.map((apn) =>
      getPropertyInfo(apn)
        .then((result) => ({ apn, success: true, data: result }))
        .catch((error) => ({ apn, success: false, error: error.message }))
    );

    const chunkResults = await Promise.all(chunkPromises);

    chunkResults.forEach((result) => {
      if (result.success) {
        results.push(result.data);
      } else {
        errors.push({ apn: result.apn, error: result.error });
      }
    });

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return {
    success: errors.length === 0,
    results,
    errors,
    summary: {
      total: apnList.length,
      successful: results.length,
      failed: errors.length,
    },
  };
};

export const convertGISGeometryToGeoJSON = (esriGeometry) => {
  if (!esriGeometry) return null;

  if (esriGeometry.rings) {
    return {
      type: 'Polygon',
      coordinates: esriGeometry.rings,
    };
  } else if (esriGeometry.paths) {
    return {
      type: 'LineString',
      coordinates: esriGeometry.paths[0],
    };
  } else if (esriGeometry.x && esriGeometry.y) {
    return {
      type: 'Point',
      coordinates: [esriGeometry.x, esriGeometry.y],
    };
  }

  return null;
};

export const calculateParcelArea = (geometry) => {
  if (!geometry || !geometry.rings) return 0;

  const ring = geometry.rings[0];
  let area = 0;

  for (let i = 0; i < ring.length - 1; i++) {
    area += ring[i][0] * ring[i + 1][1];
    area -= ring[i + 1][0] * ring[i][1];
  }

  return Math.abs(area / 2);
};

export const formatPropertyValue = (value) => {
  if (!value || value === 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatAPN = (apn) => {
  const cleaned = apn.replace(/[^0-9]/g, '');

  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return apn;
};

export const getAssessorMapInfo = (apn) => {
  // Extract map book and page from APN
  // Format: BBBBPPP-PPP where B=Book, P=Page/Parcel
  const cleaned = apn.replace(/[^0-9]/g, '');

  if (cleaned.length >= 4) {
    const mapRef = cleaned.substring(0, 4);
    return {
      mapBook: parseInt(mapRef.substring(0, 3)),
      mapPage: parseInt(mapRef.substring(3)),
      fullReference: `Book ${mapRef.substring(0, 3)}, Page ${mapRef.substring(3)}`,
      // URL to view on assessor portal (user must navigate manually)
      portalUrl: `https://portal.assessor.lacounty.gov/`,
    };
  }

  return null;
};

const laCountyAPI = {
  searchByAddress,
  searchByAPN,
  getParcelBoundary,
  getPropertyInfo,
  searchNearbyParcels,
  batchGetPropertyInfo,
  convertGISGeometryToGeoJSON,
  calculateParcelArea,
  formatPropertyValue,
  formatAPN,
  getAssessorMapInfo,
};

export default laCountyAPI;
